/**
 * 에러 토스트 자동 표시 Hook
 *
 * Query/Mutation 에러를 자동으로 토스트 알림으로 표시합니다.
 * 반복되는 에러 처리 패턴을 간소화합니다.
 *
 * **기존 패턴 (반복됨)**:
 * ```typescript
 * const { error: toastError } = useToast();
 *
 * useEffect(() => {
 *   if (error) {
 *     const errorMessage = error instanceof Error
 *       ? error.message
 *       : '작업을 실패했습니다';
 *     toastError(errorMessage);
 *   }
 * }, [error, toastError]);
 * ```
 *
 * **개선된 패턴**:
 * ```typescript
 * useErrorToast(error);
 * ```
 *
 * @module
 * @example
 * // Query 에러 자동 표시
 * function PlaylistsPage() {
 *   const { data, error } = usePlaylists();
 *   useErrorToast(error);
 *   // 에러 발생 시 자동으로 토스트 표시
 *   return <div>...</div>;
 * }
 *
 * @example
 * // 커스텀 에러 메시지
 * function TrackDetail() {
 *   const { data, error } = useTrack(trackId);
 *   useErrorToast(error, '트랙을 불러오는데 실패했습니다');
 *   return <div>...</div>;
 * }
 *
 * @example
 * // 에러 처리 비활성화
 * function SilentComponent() {
 *   const { data, error } = usePlaylists();
 *   useErrorToast(error, undefined, { enabled: false });
 *   // 직접 에러 처리
 *   return <div>...</div>;
 * }
 */

'use client';

import { useEffect, useRef } from 'react';
import { extractErrorMessage, ERROR_MESSAGES } from '@/lib/errors/messages';
import { useToast } from './useToast';

/** 기본 에러 메시지 */
const DEFAULT_ERROR_MESSAGE = ERROR_MESSAGES.API.UNKNOWN;

/** useErrorToast 옵션 */
export interface UseErrorToastOptions {
  /**
   * 토스트 표시 활성화 여부
   * @default true
   */
  enabled?: boolean;

  /**
   * 토스트 표시 시간 (밀리초)
   * @default 5000 (5초)
   */
  duration?: number;

  /**
   * 동일 에러 재표시 방지
   * @default true
   */
  preventDuplicate?: boolean;
}

/**
 * 에러 발생 시 자동으로 토스트 알림을 표시하는 Hook
 *
 * @param error - 에러 객체 (null이면 무시)
 * @param customMessage - 커스텀 에러 메시지 (선택)
 * @param options - 추가 옵션
 *
 * @example
 * // 기본 사용
 * const { error } = useQuery(...);
 * useErrorToast(error);
 *
 * @example
 * // 커스텀 메시지
 * useErrorToast(error, '플레이리스트 로딩 실패');
 */
export function useErrorToast(
  error: Error | null | undefined,
  customMessage?: string,
  options: UseErrorToastOptions = {}
): void {
  const {
    enabled = true,
    duration = 5000,
    preventDuplicate = true,
  } = options;

  const { error: showErrorToast } = useToast();
  const lastErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !error) {
      return;
    }

    const message = customMessage || extractErrorMessage(error, DEFAULT_ERROR_MESSAGE);

    // 중복 방지: 동일한 에러 메시지면 표시하지 않음
    if (preventDuplicate && lastErrorRef.current === message) {
      return;
    }

    lastErrorRef.current = message;
    showErrorToast(message, { duration });

    // 일정 시간 후 중복 방지 해제 (다음 에러 표시 가능)
    const timeoutId = setTimeout(() => {
      lastErrorRef.current = null;
    }, duration + 1000);

    return () => clearTimeout(timeoutId);
  }, [error, customMessage, enabled, duration, preventDuplicate, showErrorToast]);
}

/**
 * 여러 에러를 한 번에 처리하는 Hook
 *
 * @param errors - 에러 배열
 * @param options - 추가 옵션
 *
 * @example
 * const { error: playlistError } = usePlaylists();
 * const { error: tracksError } = useTracks();
 * useMultipleErrorToasts([playlistError, tracksError]);
 */
export function useMultipleErrorToasts(
  errors: (Error | null | undefined)[],
  options: UseErrorToastOptions = {}
): void {
  const {
    enabled = true,
    duration = 5000,
    preventDuplicate = true,
  } = options;

  const { error: showErrorToast } = useToast();
  const shownErrorsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // 실제 에러만 필터링
    const actualErrors = errors.filter((e): e is Error => !!e);

    actualErrors.forEach((error) => {
      const message = extractErrorMessage(error, DEFAULT_ERROR_MESSAGE);

      // 중복 방지
      if (preventDuplicate && shownErrorsRef.current.has(message)) {
        return;
      }

      shownErrorsRef.current.add(message);
      showErrorToast(message, { duration });
    });

    // 일정 시간 후 중복 방지 해제
    const timeoutId = setTimeout(() => {
      shownErrorsRef.current.clear();
    }, duration + 1000);

    return () => clearTimeout(timeoutId);
  }, [errors, enabled, duration, preventDuplicate, showErrorToast]);
}

export default useErrorToast;
